import { Button, Form, Input, InputNumber, message, Select, Table } from "antd";
import { collection, doc, onSnapshot, setDoc, updateDoc } from "firebase/firestore";
import { nanoid } from "nanoid";
import { useEffect, useState } from "react";
import { db } from "../firebase";
import { ReconciliationRuleType } from "../typeDefs";

const categories: Record<string, string[]> = {
  Revenue: ["Sales Revenue", "Service Revenue", "Interest Income", "Dividend Income", "Rental Income"],
  Expenses: ["Cost of Goods Sold", "Salaries and Wages", "Shareholder Salary", "Rent Expense", "Utilities Expense", "Depreciation Expense", "Interest Expense", "Insurance Expense", "Advertising Expense", "Subscriptions", "Travel and Entertainment", "Miscellaneous Expenses", "Professional Fees", "Maintenance and Repairs", "Research and Development", "Training and Development", "Office Supplies Expense"],
  Assets: ["Cash and Cash Equivalents", "Accounts Receivable", "Inventory", "Prepaid Expenses", "Property, Plant, and Equipment", "Intangible Assets", "Investments", "Office Fit-Out", "Other Current Assets"],
  Liabilities: [
    "Accounts Payable",
    "GST Payable",
    "GST Payment",
    "Income Tax Payable",
    "Income Tax Payment",
    "Provisional Tax Payment",
    "PAYE/Withholding Tax Payment",
    "Short-term Debt",
    "Long-term Debt",
    "Loan Received",
    "Loan Repayment",
    "Accrued Liabilities",
    "Deferred Revenue",
    "Notes Payable",
  ],
  Equity: ["Common Stock", "Shareholder Drawings", "Owner Contribution", "Current Account Adjustment", "Preferred Stock", "Retained Earnings", "Additional Paid-in Capital", "Treasury Stock"],
  "Non-Business": ["Personal / Other Account", "Cash Not Banked", "Duplicate / Imported In Error", "Not Company Spend", "Exclude Pending Deletion"],
};

export const ReconciliationRules = () => {
  const [rules, setRules] = useState<ReconciliationRuleType[]>([]);
  const [activeCategory, setActiveCategory] = useState("Expenses");
  const [form] = Form.useForm();

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "reconciliationRules"), (snapshot) => {
      setRules(snapshot.docs.map((item) => ({ ...item.data(), id: item.id } as ReconciliationRuleType)));
    });
    return unsub;
  }, []);

  const saveRule = () => {
    form.validateFields().then((values) => {
      const id = nanoid();
      setDoc(doc(db, `reconciliationRules/${id}`), {
        id,
        ...values,
        matchText: values.matchText.toLowerCase(),
        gstIncluded: values.gstIncluded ?? true,
        gstRegistered: values.gstRegistered ?? true,
        isDeleted: false,
        createdAt: Date.now(),
      } as ReconciliationRuleType)
        .then(() => {
          message.success("Reconciliation rule saved.");
          form.resetFields();
        })
        .catch((err) => {
          console.error(err);
          message.error("Could not save rule.");
        });
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <Form form={form} layout="vertical">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
          <Form.Item name="matchText" label="Description contains" rules={[{ required: true }]}>
            <Input placeholder="e.g. adobe" />
          </Form.Item>
          <Form.Item name="vendor" label="Vendor" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="category" label="Category" initialValue="Expenses">
            <Select onChange={setActiveCategory} options={Object.keys(categories).map((value) => ({ value, label: value }))} />
          </Form.Item>
          <Form.Item name="subCategory" label="Subcategory" initialValue="Miscellaneous Expenses">
            <Select options={(categories[activeCategory] || []).map((value) => ({ value, label: value }))} />
          </Form.Item>
          <Form.Item name="gstIncluded" label="GST Included" initialValue={true}>
            <Select options={[{ value: true, label: "Yes" }, { value: false, label: "No" }]} />
          </Form.Item>
          <Form.Item name="gstRegistered" label="GST Registered" initialValue={true}>
            <Select options={[{ value: true, label: "Yes" }, { value: false, label: "No" }]} />
          </Form.Item>
          <Form.Item name="isHomeOfficeExpense" label="Home office expense" initialValue={false}>
            <Select options={[{ value: true, label: "Yes" }, { value: false, label: "No" }]} />
          </Form.Item>
          <Form.Item name="businessUseRate" label="Business claim %" initialValue={100}>
            <InputNumber min={0} max={100} style={{ width: "100%" }} />
          </Form.Item>
        </div>
        <Button type="primary" onClick={saveRule}>Save rule</Button>
      </Form>

      <Table
        size="small"
        pagination={{ pageSize: 8 }}
        dataSource={rules.filter((rule) => !rule.isDeleted).map((rule) => ({ ...rule, key: rule.id }))}
        columns={[
          { title: "Match text", dataIndex: "matchText" },
          { title: "Vendor", dataIndex: "vendor" },
          { title: "Category", dataIndex: "category" },
          { title: "Subcategory", dataIndex: "subCategory" },
          { title: "GST", dataIndex: "gstIncluded", render: (value) => (value ? "Yes" : "No") },
          { title: "Home Office", dataIndex: "isHomeOfficeExpense", render: (value) => (value ? "Yes" : "No") },
          { title: "Business %", dataIndex: "businessUseRate", render: (value) => `${value ?? 100}%` },
          {
            title: "Actions",
            render: (_value, record) => (
              <Button danger size="small" onClick={() => updateDoc(doc(db, `reconciliationRules/${record.id}`), { isDeleted: true })}>
                Delete
              </Button>
            ),
          },
        ]}
      />
    </div>
  );
};
